/**
 * State of the patient's «دبّه» queue seats, a mirror of the queue service (dabbeh, ../queue.ts), and of
 * the subscription as Plus answered it on this page. Seats are kept in localStorage so a reload shows them
 * at once; the subscription is memory only (see `partialize`): Plus is asked again on every screen.
 * Without the service (DABBEH_API_URL empty) the actions below run the whole flow on this device (prototype).
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { DABBEH_HOLD_MINUTES, DABBEH_PRICE_TOMAN, DABBEH_QUEUE_AHEAD, DABBEH_SUBSCRIPTION_DAYS } from '../constants';
import { moveLegacyStorage } from '../legacy';
import type { DabbehDoctor, DabbehMembership, DabbehPatient, DabbehSubscription } from '../types';

// seats saved under the feature's old code name are moved before the store reads `dabbeh-state`
if (typeof window !== 'undefined') moveLegacyStorage();

const DAY = 24 * 3600_000;
const iso = (offsetMs = 0) => new Date(Date.now() + offsetMs).toISOString();
/** Eight digits, the same as the queue service's tracking codes. */
const code = () => String(Math.floor(10_000_000 + Math.random() * 90_000_000));

/** Visit time of a released appointment: tomorrow 10:20 (sample until the service provides it). */
const sampleVisitAt = () => {
  const at = new Date();
  at.setDate(at.getDate() + 1);
  at.setHours(10, 20, 0, 0);
  return at.toISOString();
};

/** Active until its end; one whose end Plus did not give is active while Plus says so. */
export const isSubscriptionActive = (sub: DabbehSubscription | null, now = Date.now()) =>
  !!sub && (sub.endsAt === undefined || new Date(sub.endsAt).getTime() > now);

interface DabbehStore {
  /** Doctor picked in the sheet, waiting for checkout. */
  draft: DabbehDoctor | null;
  /** «نوبت به نام» picked in the same sheet: the seat is joined for this person (kept through the gateway). */
  draftPatient: DabbehPatient | null;
  /** The account that picked `draftPatient` (a saved person of one account is never joined for another). */
  draftFor: string | null;
  subscription: DabbehSubscription | null;
  /** What Plus answered on this page: an active subscription, none, or not asked / not answered yet. */
  plusAnswer: 'active' | 'none' | null;
  /** Queue seats by doctor slug. */
  memberships: Record<string, DabbehMembership>;
  /** The account the service's seats were synced for (a shared phone: another account never sees them). */
  ownerId: string | null;
  /** People already waiting for a doctor before the patient joins (test tool can set 0 = empty queue). */
  queueAhead: number;
  setDraft: (doctor: DabbehDoctor, patient?: DabbehPatient | null, forUser?: string | null) => void;
  /** Buys (or renews) the subscription and seats the patient in the draft doctor's queue. */
  purchase: (paid?: DabbehSubscription | null) => DabbehMembership | null;
  /** With an active subscription: seats the patient in `doctor`'s queue, nothing to pay. */
  joinWithSubscription: (doctor: DabbehDoctor) => DabbehMembership | null;
  leave: (slug: string) => void;
  /**
   * «انصراف از این نوبت» or a missed hold: the appointment passes on, the patient goes to the end of the line.
   * `keepPosition` (the queue service is on): the place stays the last one the service gave until the next
   * sync — the device never makes up a place in line.
   */
  passOffer: (slug: string, keepPosition?: boolean) => void;
  /** The patient confirmed the held appointment (booking continues in the regular factor). */
  confirmOffer: (slug: string) => void;
  /** Test tool: put a seat in one of the states the service will send. */
  setState: (slug: string, state: 'waiting' | 'offered' | 'missed' | 'expired') => void;
  setQueueAhead: (count: number) => void;
  /** The queue service's (dabbeh) seats replace this device's (rollout doctors only), for account `ownerId`. */
  syncSeats: (seats: DabbehMembership[], ownerId?: string | null) => void;
  /**
   * The signed-in account once the site has loaded it (`null` = signed out): seats synced for another
   * account, or any seat once signed out, leave this device.
   */
  forgetOtherAccount: (userId: string | null) => boolean;
  /** The real subscription from Plus (same on every device). */
  setSubscription: (subscription: DabbehSubscription) => void;
  /** Plus says there is no active subscription: one this device still holds as active is marked ended. */
  endSubscription: () => void;
  reset: () => void;
}

const newMembership = (doctor: DabbehDoctor, ahead: number, viaSubscription: boolean, patient?: DabbehPatient | null): DabbehMembership => ({
  doctor,
  joinedAt: iso(),
  position: ahead + 1,
  trackingCode: code(),
  status: 'waiting',
  viaSubscription,
  ...(patient ? { patient } : {}),
});

export const useDabbehStore = create<DabbehStore>()(
  persist(
    (set, get) => ({
      draft: null,
      draftPatient: null,
      draftFor: null,
      subscription: null,
      plusAnswer: null,
      memberships: {},
      ownerId: null,
      queueAhead: DABBEH_QUEUE_AHEAD,
      setDraft: (doctor, patient = null, forUser = null) =>
        set({ draft: doctor, draftPatient: patient && forUser ? patient : null, draftFor: patient && forUser ? forUser : null }),
      purchase: paid => {
        const doctor = get().draft;
        if (!doctor) return null;
        const subscription: DabbehSubscription = paid ?? {
          startedAt: iso(),
          endsAt: iso(DABBEH_SUBSCRIPTION_DAYS * DAY),
          trackingCode: code(),
          pricePaid: DABBEH_PRICE_TOMAN,
        };
        const existing = get().memberships[doctor.slug];
        const membership = existing
          ? { ...existing, doctor, status: 'waiting' as const, offer: undefined, position: get().queueAhead + 1, viaSubscription: false }
          : newMembership(doctor, get().queueAhead, false, get().draftPatient);
        // just paid: active — not the «none» an earlier answer of Plus left on this page (renewal)
        set({ subscription, plusAnswer: 'active', memberships: { ...get().memberships, [doctor.slug]: membership } });
        return membership;
      },
      joinWithSubscription: doctor => {
        if (!isSubscriptionActive(get().subscription)) return null;
        const membership = get().memberships[doctor.slug] ?? newMembership(doctor, get().queueAhead, true, get().draftPatient);
        set({ draft: doctor, memberships: { ...get().memberships, [doctor.slug]: membership } });
        return membership;
      },
      leave: slug => {
        const { [slug]: _left, ...rest } = get().memberships;
        set({ memberships: rest });
      },
      passOffer: (slug, keepPosition = false) => {
        const seat = get().memberships[slug];
        if (!seat) return;
        set({
          memberships: {
            ...get().memberships,
            [slug]: {
              ...seat,
              status: 'missed',
              offer: undefined,
              position: keepPosition ? seat.position : get().queueAhead + 1,
              ...(keepPosition ? { positionPending: true } : {}),
            },
          },
        });
      },
      confirmOffer: slug => get().leave(slug),
      setState: (slug, state) => {
        const seat = get().memberships[slug];
        if (!seat) return;
        const sub = get().subscription;
        if (state === 'expired') {
          set({ subscription: sub ? { ...sub, endsAt: iso(-60_000) } : sub, memberships: { ...get().memberships, [slug]: { ...seat, status: 'waiting', offer: undefined } } });
          return;
        }
        // any other state needs a live subscription
        const live = sub && isSubscriptionActive(sub) ? sub : sub ? { ...sub, endsAt: iso(DABBEH_SUBSCRIPTION_DAYS * DAY) } : sub;
        const next: DabbehMembership =
          state === 'offered'
            ? { ...seat, status: 'offered', position: 1, offer: { visitAt: sampleVisitAt(), holdExpiresAt: iso(DABBEH_HOLD_MINUTES * 60_000) } }
            : state === 'missed'
              ? { ...seat, status: 'missed', offer: undefined, position: get().queueAhead + 1 }
              : { ...seat, status: 'waiting', offer: undefined, position: get().queueAhead + 1 };
        set({ subscription: live, memberships: { ...get().memberships, [slug]: next } });
      },
      setQueueAhead: count => set({ queueAhead: Math.max(0, count) }),
      setSubscription: subscription => set({ subscription, plusAnswer: isSubscriptionActive(subscription) ? 'active' : 'none' }),
      endSubscription: () => {
        const sub = get().subscription;
        // one seen active on this page keeps its dates («پایان اعتبار: …»), marked ended; one without
        // dates has nothing to keep
        const ended = sub && isSubscriptionActive(sub) ? (sub.endsAt ? { ...sub, endsAt: iso(-60_000) } : null) : sub;
        set({ plusAnswer: 'none', subscription: ended });
      },
      syncSeats: (seats, ownerId) =>
        set({ memberships: Object.fromEntries(seats.map(seat => [seat.doctor.slug, seat])), ...(ownerId ? { ownerId } : {}) }),
      forgetOtherAccount: userId => {
        const { memberships, ownerId } = get();
        const stale = userId === null ? Object.keys(memberships).length > 0 || ownerId !== null : !!ownerId && ownerId !== userId;
        // the person picked for «نوبت به نام» belongs to that account too
        if (stale) set({ memberships: {}, ownerId: userId, draftPatient: null, draftFor: null });
        return stale;
      },
      reset: () => set({ draft: null, draftPatient: null, draftFor: null, subscription: null, plusAnswer: null, memberships: {}, ownerId: null, queueAhead: DABBEH_QUEUE_AHEAD }),
    }),
    {
      name: 'dabbeh-state',
      version: 2,
      // the subscription is never kept on the device: Plus alone says whether it is active, on every page
      // (a copy left here once said «paid» or «ended» when Plus said otherwise)
      partialize: state => ({ draft: state.draft, draftPatient: state.draftPatient, draftFor: state.draftFor, memberships: state.memberships, ownerId: state.ownerId, queueAhead: state.queueAhead }),
      migrate: persisted => {
        const { subscription: _dropped, ...rest } = (persisted ?? {}) as Partial<DabbehStore>;
        return rest as DabbehStore;
      },
    },
  ),
);
